import React from "react";
import clsx from "clsx";
import styles from "./Text.module.scss";

export type IText = React.HTMLAttributes<
  HTMLParagraphElement | HTMLSpanElement | HTMLDivElement
> & {
  color?: "default" | "faded" | "no-results" | "inherit";
  element: "p" | "span" | "div";
  size?: "base" | "small" | "large" | "smallest";
  weight?: "regular" | "medium" | "bold";
};

const TextElement = ({
  element,
  children,
  ...props
}: { element: "p" | "span" | "div" } & React.HTMLAttributes<
  HTMLParagraphElement | HTMLSpanElement | HTMLDivElement
>) => {
  const htmlElement = element;
  return React.createElement(htmlElement, props, children);
};

export const Text = ({
  children,
  color = "default",
  element,
  size = "base",
  weight = "regular",
  ...props
}: IText) => {
  if (children === undefined || children === null) {
    return <></>;
  }

  return (
    <TextElement
      element={element}
      {...props}
      className={clsx(
        styles[`color-${color}`],
        styles[`element-${element}`],
        styles[`size-${size}`],
        styles[`weight-${weight}`],
        props.className,
      )}
    >
      {children}
    </TextElement>
  );
};

type ITextWithLineBreaks = StrictOmit<IText, "element"> & {
  children: string | null | undefined;
};

export const TextWithLineBreaks = ({
  children,
  className,
  color = "default",
  size = "base",
  weight = "regular",
  ...props
}: ITextWithLineBreaks) => {
  if (children === undefined || children === null) {
    return <></>;
  }

  const textAsArray = children.split("\n");

  if (textAsArray.length === 1) {
    return (
      <Text element="p" color={color} size={size} weight={weight} {...props}>
        {children}
      </Text>
    );
  }

  return (
    <ul
      {...props}
      className={clsx(
        styles[`color-${color}`],
        styles[`element-ul`],
        styles[`size-${size}`],
        styles[`weight-${weight}`],
        className,
      )}
    >
      {textAsArray.map((item, index) => (
        <li key={item + index}>
          <Text element="p" color={color} size={size} weight={weight}>
            {item}
          </Text>
        </li>
      ))}
    </ul>
  );
};

type ITextWithEllipsis = IText & {
  maxWidth:
    | `${number}${"px" | "em" | "rem" | "%" | "vw" | "vh" | "ch"}`
    | "unset";
  title: string;
};

export const TextWithEllipsis = ({
  children,
  color = "default",
  size = "base",
  weight = "regular",
  maxWidth,
  ...props
}: ITextWithEllipsis) => {
  if (children === undefined || children === null) {
    return <></>;
  }

  return (
    <Text
      color={color}
      size={size}
      weight={weight}
      {...props}
      className={styles.ellipsis}
      style={{
        maxWidth,
        ...props.style,
      }}
    >
      {children}
    </Text>
  );
};
